import { useCallback, useMemo, useRef, useState } from "react";
import { fetchTaskById } from "@/client/api/tasksSelf";
import { mapTaskSelfItemToTask } from "@/client/store/taskSelfStore";
import type { Task } from "@/client/hooks/useTaskManager";
import { TaskStatus } from "@/client/hooks/useTaskManager";
import type { CsvSubmittedTaskRow } from "../types";

export interface BatchResultRecord {
  row: CsvSubmittedTaskRow;
  task: Task | null;
  loading: boolean;
  error: string | null;
  fetchedAt: number | null;
}

export interface BatchResultProgressSummary {
  total: number;
  completed: number;
  failed: number;
  running: number;
  pending: number;
  unknown: number;
}

export interface UseBatchResultProgressReturn {
  records: BatchResultRecord[];
  loading: boolean;
  lastUpdatedAt: number | null;
  summary: BatchResultProgressSummary;
  loadRows: (rows: CsvSubmittedTaskRow[]) => Promise<void>;
  refresh: () => Promise<void>;
  clear: () => void;
}

const initialSummary: BatchResultProgressSummary = {
  total: 0,
  completed: 0,
  failed: 0,
  running: 0,
  pending: 0,
  unknown: 0,
};

const buildInitialRecords = (rows: CsvSubmittedTaskRow[]): BatchResultRecord[] =>
  rows.map((row) => ({
    row,
    task: null,
    loading: true,
    error: null,
    fetchedAt: null,
  }));

const classifyStatus = (task: Task | null): keyof BatchResultProgressSummary => {
  if (!task) return "unknown";
  switch (task.status) {
    case TaskStatus.SUCCESS:
      return "completed";
    case TaskStatus.FAILED:
      return "failed";
    case TaskStatus.RUNNING:
      return "running";
    case TaskStatus.PENDING:
    case TaskStatus.SUBMITTING:
      return "pending";
    default:
      return "unknown";
  }
};

export const useBatchResultProgress = (): UseBatchResultProgressReturn => {
  const [records, setRecords] = useState<BatchResultRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const rowsRef = useRef<CsvSubmittedTaskRow[]>([]);

  const fetchRecords = useCallback(async (rows: CsvSubmittedTaskRow[]) => {
    if (!rows.length) {
      setRecords([]);
      setLastUpdatedAt(null);
      return;
    }

    setLoading(true);
    setRecords(buildInitialRecords(rows));
    rowsRef.current = rows;

    const results = await Promise.all(
      rows.map(async (row): Promise<BatchResultRecord> => {
        try {
          const item = await fetchTaskById(row.taskId);
          if (!item) {
            return {
              row,
              task: null,
              loading: false,
              error: "未查询到任务记录。",
              fetchedAt: Date.now(),
            };
          }
          const task = mapTaskSelfItemToTask(item);
          return {
            row,
            task,
            loading: false,
            error: null,
            fetchedAt: Date.now(),
          };
        } catch (error) {
          const message =
            error instanceof Error && error.message ? error.message : "任务查询失败。";
          return {
            row,
            task: null,
            loading: false,
            error: message,
            fetchedAt: Date.now(),
          };
        }
      })
    );

    setRecords(results);
    setLoading(false);
    setLastUpdatedAt(Date.now());
  }, []);

  const loadRows = useCallback(async (rows: CsvSubmittedTaskRow[]) => {
    await fetchRecords(rows);
  }, [fetchRecords]);

  const refresh = useCallback(async () => {
    const rows = rowsRef.current;
    if (!rows.length) return;
    await fetchRecords(rows);
  }, [fetchRecords]);

  const clear = useCallback(() => {
    rowsRef.current = [];
    setRecords([]);
    setLoading(false);
    setLastUpdatedAt(null);
  }, []);

  const summary = useMemo<BatchResultProgressSummary>(() => {
    if (!records.length) {
      return initialSummary;
    }
    return records.reduce<BatchResultProgressSummary>(
      (acc, record) => {
        const category = classifyStatus(record.task);
        acc.total += 1;
        acc[category] += 1;
        return acc;
      },
      { ...initialSummary }
    );
  }, [records]);

  return {
    records,
    loading,
    lastUpdatedAt,
    summary,
    loadRows,
    refresh,
    clear,
  };
};

export default useBatchResultProgress;
